import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  ButtonGroup,
} from '@mui/material';
import toast from 'react-hot-toast';

import { settingsService, type MerchantSettings } from '@/services/settingsService';
import { useNavigate } from 'react-router-dom';

const DEFAULT_WEBHOOK_RETRY = 10;
const DEFAULT_WEBHOOK_INTERVAL = 3600;

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<any>();
  const [originalSettings, setOriginalSettings] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ retry?: string; interval?: string }>({});


//   {
//     "success": true,
//     "message": "Merchant settings fetched successfully!",
//     "merchant_config": {
//         "is_webhook_enabled": false,
//         "webhook_url": "https://webhook.site/c2b584ab-d8e0-4b00-82bb-8ffa2f5f48c1",
//         "transaction_webhook_retry": 3,
//         "transaction_webhook_interval": 300
//     }
// }

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getSettings();
      setSettings(response.merchant_config);
      setOriginalSettings(response.merchant_config);
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await settingsService.updateSettings(settings);
      toast.success(response.message || 'Settings saved successfully!');
      setOriginalSettings(settings);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  const validateField = (field: string, value: number) => {
    if (field === 'transaction_webhook_retry') {
      if (value < 0) return 'Retry count cannot be negative';
      if (value > 100) return 'Retry count cannot exceed 100';
      if (!Number.isInteger(value)) return 'Retry count must be a whole number';
      return '';
    }
    if (field === 'transaction_webhook_interval') {
      if (value < 1) return 'Interval must be at least 1 second';
      if (value > 86400) return 'Interval cannot exceed 86400 seconds (24 hours)';
      if (!Number.isInteger(value)) return 'Interval must be a whole number';
      return '';
    }
    return '';
  };

  const hasValidationErrors = () => {
    return Object.values(errors).some(error => error !== '');
  };

  const handleChange = (field: keyof MerchantSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.type === 'number'
        ? Number(event.target.value)
        : event.target.value;

    // Validate numeric fields
    if (field === 'transaction_webhook_retry') {
      const error = validateField(field, value as number);
      setErrors(prev => ({ ...prev, retry: error }));
    }
    if (field === 'transaction_webhook_interval') {
      const error = validateField(field, value as number);
      setErrors(prev => ({ ...prev, interval: error }));
    }

    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Webhook Configurations
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.is_webhook_enabled || false}
                  onChange={handleChange('is_webhook_enabled')}
                />
              }
              label="Enable Webhooks"
            />

            <TextField
              label="Webhook URL"
              value={settings?.webhook_url || ''}
              onChange={handleChange('webhook_url')}
              fullWidth
              disabled={!settings?.is_webhook_enabled}
              placeholder="https://your-domain.com/webhook"
              size="small"
            />

            <TextField
              label="Transaction Webhook Retry"
              type="number"
              value={
                settings?.transaction_webhook_retry === ''
                  ? ''
                  : settings?.transaction_webhook_retry ?? DEFAULT_WEBHOOK_RETRY
              }
              onChange={handleChange('transaction_webhook_retry')}
              disabled={!settings?.is_webhook_enabled}
              inputProps={{ min: 0, max: 100 }}
              error={!!errors.retry}
              helperText={errors.retry || `Default: ${DEFAULT_WEBHOOK_RETRY}`}
              size="small"
            />

            <TextField
              label="Transaction Webhook Interval (seconds)"
              type="number"
              value={settings?.transaction_webhook_interval ?? DEFAULT_WEBHOOK_INTERVAL}
              onChange={handleChange('transaction_webhook_interval')}
              disabled={!settings?.is_webhook_enabled}
              inputProps={{ min: 1, max: 86400 }}
              error={!!errors.interval}
              helperText={errors.interval || `Default: ${DEFAULT_WEBHOOK_INTERVAL}`}
              size="small"
            />


            <ButtonGroup sx={{  alignSelf: 'flex-end' }}>
              <Box sx={{ display: 'flex', gap: 2, alignSelf: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/settings')}
                  disabled={loading}

                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !hasChanges() || hasValidationErrors()}
                  sx={{ alignSelf: 'flex-end' }}
                >
                  {saving ? <CircularProgress size={20} /> : 'Save Settings'}
                </Button>
              </Box>
            </ButtonGroup>



          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SettingsPage;